import { useCallback, useReducer, type Dispatch, type SetStateAction } from 'react'
import { ICv } from 'types/cvsInterfaces'
import { IError } from 'types/error'
import { IPerson } from 'types/userInterfaces'
import type { CvProgressResponse } from 'types/cvsInterfaces'
import { ProfileState, ProfileAction } from 'types/profileInterfaces'

const initialProfileState: ProfileState = {
  personData: undefined,
  selectedCV: undefined,
  backendError: undefined,
  cvUpdatedAt: new Date(),
  isEmpty: true,
  personDataLoading: false,
  emptyLoading: false,
  cvLoading: true,
  fullCvLoading: false,
  teamAccessLoading: false,
  copy: false,
  edit: undefined,
  refresh: false,
  original: undefined,
  progressModal: false,
  profileProgress: undefined,
  fullCV: undefined,
  openHistory: undefined,
  updateCvProgress: undefined,
  progressCV: undefined,
  personTeamAccess: false,
  cvParse: false,
  parseCredits: 0,
  sectionLoadingState: {
    employers: true,
    projects: true,
    education: true,
    leisure_activities: true,
    courses: true,
    certificates: true,
    competences: true,
    language_skills: true,
    references: true,
    urls: true,
    person_skills: true,
    industries: true,
    roles: true,
  },
}

const profileReducer = (state: ProfileState, action: ProfileAction): ProfileState => {
  switch (action.type) {
    case 'setField': {
      const currentValue = state[action.key]
      const nextValue =
        typeof action.value === 'function'
          ? (action.value as (value: ProfileState[keyof ProfileState]) => ProfileState[keyof ProfileState])(
              currentValue
            )
          : action.value

      if (Object.is(currentValue, nextValue)) {
        return state
      }

      return {
        ...state,
        [action.key]: nextValue,
      }
    }
    case 'patch': {
      const changedKeys = Object.keys(action.payload) as Array<keyof ProfileState>
      const hasChanges = changedKeys.some(key => !Object.is(state[key], action.payload[key]))

      if (!hasChanges) {
        return state
      }

      return {
        ...state,
        ...action.payload,
      }
    }
    default:
      return state
  }
}

export const useProfileState = () => {
  const [state, dispatch] = useReducer(profileReducer, initialProfileState)

  const setField = useCallback(<K extends keyof ProfileState>(key: K, value: SetStateAction<ProfileState[K]>) => {
    dispatch({ type: 'setField', key, value })
  }, [])

  const patch = useCallback((payload: Partial<ProfileState>) => {
    dispatch({ type: 'patch', payload })
  }, [])

  const setPersonData: Dispatch<SetStateAction<IPerson | undefined>> = useCallback(
    value => setField('personData', value),
    [setField]
  )
  const setSelectedCV: Dispatch<SetStateAction<ICv | undefined>> = useCallback(
    value => setField('selectedCV', value),
    [setField]
  )
  const setBackendError: Dispatch<SetStateAction<IError | undefined>> = useCallback(
    value => setField('backendError', value),
    [setField]
  )
  const setCvUpdatedAt: Dispatch<SetStateAction<Date>> = useCallback(
    value => setField('cvUpdatedAt', value),
    [setField]
  )
  const setIsEmpty: Dispatch<SetStateAction<boolean>> = useCallback(value => setField('isEmpty', value), [setField])
  const setCvLoading: Dispatch<SetStateAction<boolean>> = useCallback(value => setField('cvLoading', value), [setField])
  const setFullCvLoading: Dispatch<SetStateAction<boolean>> = useCallback(
    value => setField('fullCvLoading', value),
    [setField]
  )
  const setCopy: Dispatch<SetStateAction<boolean>> = useCallback(value => setField('copy', value), [setField])
  const setEdit: Dispatch<SetStateAction<ICv | undefined>> = useCallback(value => setField('edit', value), [setField])
  const setProgressModal: Dispatch<SetStateAction<boolean>> = useCallback(
    value => setField('progressModal', value),
    [setField]
  )
  const setProfileProgress: Dispatch<SetStateAction<number | undefined>> = useCallback(
    value => setField('profileProgress', value),
    [setField]
  )
  const setFullCV: Dispatch<SetStateAction<ICv | undefined>> = useCallback(
    value => setField('fullCV', value),
    [setField]
  )
  const setOpenHistory: Dispatch<SetStateAction<number | undefined>> = useCallback(
    value => setField('openHistory', value),
    [setField]
  )
  const setUpdateCvProgress: Dispatch<SetStateAction<Date | undefined>> = useCallback(
    value => setField('updateCvProgress', value),
    [setField]
  )
  const setProgressCV: Dispatch<SetStateAction<CvProgressResponse | undefined>> = useCallback(
    value => setField('progressCV', value),
    [setField]
  )
  const setCvParse: Dispatch<SetStateAction<boolean>> = useCallback(value => setField('cvParse', value), [setField])

  return {
    state,
    setField,
    patch,
    setPersonData,
    setSelectedCV,
    setBackendError,
    setCvUpdatedAt,
    setIsEmpty,
    setCvLoading,
    setFullCvLoading,
    setCopy,
    setEdit,
    setProgressModal,
    setProfileProgress,
    setFullCV,
    setOpenHistory,
    setUpdateCvProgress,
    setProgressCV,
    setCvParse,
  }
}
